import type { FragmentType } from 'src/gql';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { useEffect, useEffectEvent, useState } from 'react';
import { Text } from '@savvy-kit/foundations/text';
import { ArrowLeftRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { UpdateType } from 'src/constants/portfolio';
import { graphql, unmaskFragment } from 'src/gql';
import { isDisconnectedLinkedAccount, isPlaidLinkedAccount } from 'src/utils/account';

import { errorColors, successColors } from '@savvy/deprecated-shared/theme';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

export const LinkedAccountContentFragment = graphql(/* GraphQL */ `
  fragment LinkedAccountContent on Account {
    ...IsDisconnectedLinkedAccount
    id
    lastUpdatedAt
    providerAccount {
      providerName
      source
    }
    providerAccountId
  }
`);

const ACCOUNT_QUERY = graphql(/* GraphQL */ `
  query Account($id: ID!) {
    account(id: $id) {
      ...LinkedAccountContent
    }
  }
`);

export default function LinkedAccountContent({
  accountFrag,
  onEdit,
}: {
  readonly accountFrag: FragmentType<typeof LinkedAccountContentFragment>;
  readonly onEdit: Deprecated_HandleItemUpdate;
}) {
  const account = unmaskFragment(LinkedAccountContentFragment, accountFrag);
  const [refetchIntervalId, setRefetchIntervalId] = useState<NodeJS.Timeout | null>(null);

  const { data: maskedAccountData, previousData: maskedPreviousAccountData } = useQueryWithErrorLogging(ACCOUNT_QUERY, {
    variables: {
      id: account.id,
    },
  });
  const accountData = unmaskFragment(LinkedAccountContentFragment, maskedAccountData?.account);
  const previousAccountData = unmaskFragment(LinkedAccountContentFragment, maskedPreviousAccountData?.account);

  const accountDataToRender = accountData ? accountData : account;

  const handleEdit = useEffectEvent((args: Parameters<Deprecated_HandleItemUpdate>[0]) => {
    onEdit(args);
  });

  useEffect(() => {
    if (
      !!refetchIntervalId &&
      previousAccountData &&
      accountData?.lastUpdatedAt !== previousAccountData.lastUpdatedAt
    ) {
      toast.success(`Refreshed ${account.providerAccount?.providerName}`);
      clearInterval(refetchIntervalId);
      setRefetchIntervalId(null);
      handleEdit({
        type: UpdateType.REFRESH_PROVIDER_ACCOUNT,
        id: accountDataToRender.providerAccountId ?? '',
      });
    }
  }, [previousAccountData, accountData, refetchIntervalId]);

  return (
    <>
      {!isDisconnectedLinkedAccount({ accountFrag: account }) &&
        isPlaidLinkedAccount({
          providerAccountSource: account.providerAccount?.source,
        }) && (
          <div className="flex flex-row gap-4 py-6">
            <ArrowLeftRight color={successColors.main} />
            <Text variant="body-sm">Connected with Plaid</Text>
          </div>
        )}
      {/*If provider linked account is disconnected*/}
      {isDisconnectedLinkedAccount({ accountFrag: account }) &&
        isPlaidLinkedAccount({
          providerAccountSource: account.providerAccount?.source,
        }) && (
          <>
            <div className="flex flex-row gap-4 py-6">
              <ArrowLeftRight color={errorColors.main} />
              <Text variant="body-sm">Disconnected from Plaid</Text>
            </div>

            <div>
              <Text variant="body-sm">
                Plaid has stopped sending us information about this account. Reconnect to keep it up to date.
              </Text>
            </div>
          </>
        )}
    </>
  );
}
